import { assign, fromPromise, setup } from 'xstate';
import { showAxiosErrorAction } from '~/features/actions';
import type { ListingActivationPreview } from '~/features/employer/billing/types';
import { hasValidCostCalculation } from '~/features/market/listings/components/costCalculatorMoney';
import { isPaymentMethodRequired } from '../../listing-search/errors';
import { showListingErrorAction } from '../listingConflicts';
import {
  fallbackListingFeePreview,
  listingFeeIsDue,
  showFeePreviewError,
  warnPossibleDuplicates,
} from '../listingFee';
import { msaasAdditionalTermsMachine } from '../shared-steps/additional-terms/additionalTerms.machine';
import { msaasFacilitiesMachine } from '../shared-steps/facilities/facilities.machine';
import {
  addDetail,
  addListing,
  clearDeactivating,
  closeModal,
  deleteListing,
  nextStep,
  openDeactivateConfirmation,
  openDeleteConfirmation,
  openModal,
  openRelistedDraft,
  prevStep,
  resetLoading,
  resetStep,
  setDeactivating,
  setListingCoverage,
  setListingFacilities,
  setListingFacilityStep,
  setListings,
  setListingStates,
  setListingToCancel,
  setListingToDeactivate,
  setListingToDelete,
  setListingToEdit,
  setListingToRelist,
  setLoading,
  setRelistFromCancel,
  showDeleteError,
  updateDetail,
  updateListing,
} from './actions';
import {
  API_CALLS,
  cancelListingById,
  deactivateListing,
  deactivateListingById,
  getSummary,
  previewActivationFee,
  relistListingById,
  submitListing,
  SUMMARY_ROUTES,
} from './api';
import { Context, Events, Scope } from './types';
import { basicInfoMachine } from './wizard-steps/basic-info/basicInfo.machine';
import { compensationMachine } from './wizard-steps/compensation/compensation.machine';
import { laborMetricsMachine } from './wizard-steps/labor-metrics/laborMetrics.machine';
import { coverageMachine } from './wizard-steps/offering-coverage/coverage.machine';
import { shiftDetailsMachine } from './wizard-steps/shift-details/shiftDetails.machine';

const WIZARD_STEPS = [
  '$_BASIC_INFO',
  '$_FACILITIES',
  '$_LABOR_METRICS',
  '$_COMPENSATION',
  '$_SHIFT_DETAILS',
  '$_COST_CALCULATOR',
  '$_ADDITIONAL_INFO',
  '$_REVIEW',
];

export const msaasListingsMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  actions: {
    openModal: assign(openModal),
    closeModal: assign(closeModal),
    nextStep: assign(nextStep),
    prevStep: assign(prevStep),
    resetStep: assign(resetStep),
    setMsaasListings: assign(setListings),
    setMsaasListingToEdit: assign(setListingToEdit),
    setMsaasListingToDelete: assign(setListingToDelete),
    setMsaasListingToDeactivate: assign(setListingToDeactivate),
    setDeactivating: assign(setDeactivating),
    clearDeactivating: assign(clearDeactivating),
    addMsaasListing: assign(addListing),
    updateMsaasListing: assign(updateListing),
    deleteMsaasListing: assign(deleteListing),
    setLoading: assign(setLoading),
    resetLoading: assign(resetLoading),
    setListingFacilityStep: assign(setListingFacilityStep),
    setMsaasJobListingFacilities: assign(setListingFacilities),
    setListingStates: assign(setListingStates),
    setListingCoverage: assign(setListingCoverage),
    setMsaasListingToCancel: assign(setListingToCancel),
    setMsaasListingToRelist: assign(setListingToRelist),
    setRelistFromCancel: assign(setRelistFromCancel),
    openRelistedDraft: assign(openRelistedDraft),
    showError: showAxiosErrorAction,
    // [MCV-1889] Lifecycle calls can answer 409 CONTRACT_EXISTS / LISTING_FILLED / IMMUTABLE_FIELD.
    showListingError: showListingErrorAction,
    updateMsaasDetail: assign(updateDetail),
    addMsaasDetail: assign(addDetail),
    sendDeleteConfirmation: openDeleteConfirmation,
    sendDeactivateConfirmation: openDeactivateConfirmation,
    showDeleteError,
    // [MCV-1933] Listing-fee confirmation and the non-blocking duplicate warning.
    setFeePreview: assign({
      feePreview: ({ event }) => (event as unknown as { output: ListingActivationPreview }).output,
    }),
    clearFeePreview: assign({ feePreview: undefined }),
    markPaymentMethodMissing: assign({
      feePreview: ({ context }) => ({
        ...(context.feePreview ?? fallbackListingFeePreview(false)),
        paymentMethodOnFile: false,
      }),
    }),
    warnPossibleDuplicates,
    showFeePreviewError,
  },
  guards: {
    relistsAfterCancel: ({ context }) => context.relistAfterCancel,
    canSubmitListing: ({ context }) => hasValidCostCalculation(context.listing?.detail),
  },
  actors: {
    basicInfoRef: basicInfoMachine,
    facilitiesStepRef: msaasFacilitiesMachine,
    coverageStepRef: coverageMachine,
    laborMetricsRef: laborMetricsMachine,
    compensationStepRef: compensationMachine,
    shiftDetailsRef: shiftDetailsMachine,
    additionalTermsRef: msaasAdditionalTermsMachine,
    getMsaasListings: fromPromise(API_CALLS.get),
    getMsaasListingDetail: fromPromise(SUMMARY_ROUTES.post),
    deleteMsaasListing: fromPromise(API_CALLS.delete),
    submitListing: fromPromise(submitListing),
    previewActivationFee: fromPromise(previewActivationFee),
    deactivateListing: fromPromise(deactivateListing),
    deactivateListingById: fromPromise(deactivateListingById),
    cancelListingById: fromPromise(cancelListingById),
    relistListingById: fromPromise(relistListingById),
    getSummary: fromPromise(getSummary),
  },
}).createMachine({
  context: ({ spawn, self, input }) => ({
    employerUser: undefined,
    currentStep: 0,
    parentRef: input?.parentRef,
    basicInfoRef: spawn('basicInfoRef', {
      id: 'basicInfoState',
      input: { parentRef: self },
    }),
    facilitiesStepRef: spawn('facilitiesStepRef', {
      id: 'msaasFacilitiesState',
      input: { parentRef: self },
    }),
    coverageStepRef: spawn('coverageStepRef', {
      id: 'msaasCoverageState',
      input: { parentRef: self },
    }),
    laborMetricsRef: spawn('laborMetricsRef', {
      id: 'laborMetricsState',
      input: { parentRef: self },
    }),
    compensationStepRef: spawn('compensationStepRef', {
      id: 'compensationStepState',
      input: { parentRef: self },
    }),
    shiftDetailsRef: spawn('shiftDetailsRef', {
      id: 'shiftDetailsState',
      input: { parentRef: self },
    }),
    additionalTermsRef: spawn('additionalTermsRef', {
      id: 'msaasAdditionalTermsState',
      input: { parentRef: self },
    }),
    listings: [],
    listing: undefined,
    loading: true,
    idToDelete: -1,
    idToDeactivate: -1,
    deactivating: false,
    idToCancel: -1,
    idToRelist: -1,
    relistAfterCancel: false,
    modal: {
      edit: false,
      create: false,
    },
    summary: undefined,
    feePreview: undefined,
  }),
  initial: '$_VIEW',
  on: {
    ON_SET_EMPLOYER_USER: {
      actions: assign({
        employerUser: ({ event }) => event.payload.employerUser,
      }),
      target: '.$_VIEW',
    },
    ON_LOAD: [
      // [MCV-1587] Must come first: BOTH branches below are targeted, so an
      // ON_LOAD landing mid-PATCH would exit $_DEACTIVATE_ROW -- running
      // `clearDeactivating` (destroying the ON_DEACTIVATE serialization) and
      // stopping the invoked actor while the PATCH, which carries no
      // AbortSignal, keeps running server-side with nobody observing it. The
      // GET could then paint the row as still Active, and the correcting
      // reload would never run.
      //
      // This is reachable by ordinary navigation, not a corner case: the
      // listings actor is spawned into marketState's context rather than by
      // the route, so it outlives the page, and $_MSAAS_LISTINGS_VIEW re-sends
      // ON_LOAD on every entry -- including a sidebar click on the page you
      // are already on.
      //
      // Internal (no target): the newest employerUser is still recorded, the
      // invoke is left alone, and $_DEACTIVATE_ROW's own onDone -> $_LOAD
      // supplies the reload this event was asking for -- a strictly better one,
      // because it is guaranteed to run after the commit.
      {
        guard: ({ context }) => context.deactivating,
        actions: [
          assign({
            employerUser: ({ context, event }) =>
              event?.payload?.employerUser ?? context.employerUser,
          }),
        ],
      },
      {
        guard: ({ context, event }) =>
          !(event?.payload?.employerUser?.employerId ?? context.employerUser?.employerId),
        target: '.$_VIEW',
        actions: ['resetLoading'],
      },
      {
        target: '.$_LOAD',
        actions: [
          assign({
            employerUser: ({ context, event }) =>
              event?.payload?.employerUser ?? context.employerUser,
          }),
          'setLoading',
        ],
      },
    ],
    ON_SUBMIT_LISTING: {
      guard: 'canSubmitListing',
      target: '.$_FEE_PREVIEW',
      actions: 'setLoading',
    },
    ON_SAVE_DRAFT: {
      target: '.$_DEACTIVATE',
      actions: 'setLoading',
    },
    ON_SEND_DEACTIVATE_CONFIRMATION: {
      guard: ({ context }) => !context.deactivating,
      actions: 'sendDeactivateConfirmation',
    },
    // [MCV-1587] Root-level, matching ON_DELETE's placement in the job-listing
    // machine: the Table's `loading` comes from the user machine, not this
    // machine's context.loading, so rows stay clickable during a $_LOAD
    // refetch. Scoping this to $_VIEW would silently drop those clicks.
    ON_DEACTIVATE: {
      // Serialized: the PATCH carries no AbortSignal, so a second click would
      // re-enter $_DEACTIVATE_ROW and replace the invoked actor while the
      // first request kept running server-side. Its result -- including an
      // error -- would then be dropped, and whichever reload landed last
      // would decide what the user sees. One at a time; $_LOAD after each
      // PATCH re-syncs the table.
      guard: ({ context }) => !context.deactivating,
      target: '.$_DEACTIVATE_ROW',
      actions: ['setMsaasListingToDeactivate', 'setLoading'],
    },
    // [MCV-1889] Cancel and relist reuse the row-lifecycle serialization (`deactivating`), for the
    // same reason ON_DEACTIVATE has it: the POSTs carry no AbortSignal, so a second click must not
    // replace the invoked actor and orphan the first request.
    ON_CANCEL_LISTING: {
      guard: ({ context }) => !context.deactivating,
      target: '.$_CANCEL_ROW',
      actions: ['setMsaasListingToCancel', 'setLoading'],
    },
    ON_RELIST: {
      guard: ({ context }) => !context.deactivating,
      target: '.$_RELIST_ROW',
      actions: ['setMsaasListingToRelist', 'setLoading'],
    },
    ON_CLOSE_MODAL: {
      target: '.$_LOAD',
      actions: 'closeModal',
    },
    ON_ADD_LISTING: {
      actions: 'addMsaasListing',
    },
    ON_UPDATE_LISTING: {
      actions: 'updateMsaasListing',
    },
    ON_UPDATE_FACILITIES: {
      actions: 'setMsaasJobListingFacilities',
    },
    ON_UPDATE_FACILITY_STEP: {
      actions: 'setListingFacilityStep',
    },
    ON_UPDATE_STATES: {
      actions: 'setListingStates',
    },
    ON_UPDATE_COVERAGE: {
      actions: 'setListingCoverage',
    },
    ON_ADD_DETAIL: {
      actions: 'addMsaasDetail',
    },
    ON_UPDATE_DETAIL: {
      actions: ['updateMsaasDetail'],
    },
  },
  states: {
    $_VIEW: {
      on: {
        ON_OPEN_MODAL: {
          target: '$_BASIC_INFO',
          actions: 'openModal',
        },
        ON_EDIT: {
          target: '$_BASIC_INFO',
          actions: ['openModal', 'setMsaasListingToEdit'],
        },
        ON_SEND_DELETE_CONFIRMATION: {
          actions: 'sendDeleteConfirmation',
        },
        ON_DELETE: {
          target: '$_DELETE',
          actions: 'setMsaasListingToDelete',
        },
      },
    },
    // [MCV-1933] Ask what publishing costs before activating. Only an explicit charge interrupts;
    // a failed preview returns to review without publishing or charging.
    $_FEE_PREVIEW: {
      invoke: {
        id: 'previewFee',
        src: 'previewActivationFee',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: [
          {
            guard: ({ event }) => listingFeeIsDue(event.output),
            target: '$_FEE_CONFIRM',
            actions: ['setFeePreview', 'resetLoading'],
          },
          { target: '$_ACTIVATE' },
        ],
        onError: { target: '$_REVIEW', actions: ['resetLoading', 'showFeePreviewError'] },
      },
    },
    $_FEE_CONFIRM: {
      on: {
        ON_CONFIRM_FEE: {
          guard: ({ context }) => context.feePreview?.paymentMethodOnFile !== false,
          target: '$_ACTIVATE',
          actions: 'setLoading',
        },
        ON_CANCEL_FEE: {
          target: '$_REVIEW',
          actions: ['clearFeePreview', 'resetLoading'],
        },
      },
    },
    $_ACTIVATE: {
      invoke: {
        id: 'submit',
        src: 'submitListing',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_LOAD',
          actions: ['closeModal', 'clearFeePreview', 'warnPossibleDuplicates'],
        },
        onError: [
          {
            // 402: a fee is due and there is no payment method (or it lapsed since the preview).
            guard: ({ event }) => isPaymentMethodRequired(event.error),
            target: '$_FEE_CONFIRM',
            actions: ['markPaymentMethodMissing', 'resetLoading'],
          },
          {
            target: '$_VIEW',
            actions: ['showListingError', 'clearFeePreview', 'resetLoading'],
          },
        ],
      },
    },
    $_DEACTIVATE: {
      invoke: {
        id: 'deactivate',
        src: 'deactivateListing',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_LOAD',
          actions: 'closeModal',
        },
        onError: {
          target: '$_VIEW',
          actions: ['showError', 'resetLoading'],
        },
      },
    },
    // [MCV-1587] Separate from $_DEACTIVATE, which serves ON_SAVE_DRAFT and
    // reads context.listing. Deliberately no closeModal on onDone (unlike
    // $_DEACTIVATE): there is no modal to close on this path, and nulling
    // context.listing would be a side effect this transition has no reason to
    // cause.
    //
    // (An earlier version of this comment claimed the wizard could be open
    // here. It cannot: the only ON_DEACTIVATE senders are the table row button
    // and the delete-confirmation dialog, and the wizard's ModalDialog is a
    // Headless UI Dialog whose full-viewport backdrop makes both
    // unclickable. That also rules out ON_CLOSE_MODAL as a second way to exit
    // this state mid-PATCH.)
    $_DEACTIVATE_ROW: {
      entry: 'setDeactivating',
      exit: 'clearDeactivating',
      invoke: {
        id: 'deactivateRow',
        src: 'deactivateListingById',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_LOAD',
        },
        onError: {
          target: '$_VIEW',
          actions: ['showError', 'resetLoading'],
        },
      },
    },
    // [MCV-1889] Cancels a hand-filled listing. With the relist box ticked it carries straight on to
    // the relist; a 409 CONTRACT_EXISTS (it was filled by a contract after all) is explained.
    $_CANCEL_ROW: {
      entry: 'setDeactivating',
      exit: 'clearDeactivating',
      invoke: {
        id: 'cancelRow',
        src: 'cancelListingById',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: [
          {
            guard: 'relistsAfterCancel',
            target: '$_RELIST_ROW',
            actions: 'setRelistFromCancel',
          },
          { target: '$_LOAD' },
        ],
        onError: {
          target: '$_LOAD',
          actions: 'showListingError',
        },
      },
    },
    // [MCV-1889] Copies a cancelled listing to a new draft and opens it in the matching wizard.
    $_RELIST_ROW: {
      entry: 'setDeactivating',
      exit: 'clearDeactivating',
      invoke: {
        id: 'relistRow',
        src: 'relistListingById',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_BASIC_INFO',
          actions: ['openRelistedDraft', 'resetLoading'],
        },
        onError: {
          target: '$_LOAD',
          actions: 'showListingError',
        },
      },
    },
    $_LOAD: {
      entry: ['setLoading'],
      invoke: {
        id: 'get',
        src: 'getMsaasListings',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: ['setMsaasListings', 'resetLoading'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['showError', 'resetLoading'],
        },
      },
    },
    $_DELETE: {
      invoke: {
        id: 'delete',
        src: 'deleteMsaasListing',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: ['deleteMsaasListing'],
        },
        onError: {
          target: '$_VIEW',
          // [MCV-1587] Delete-specific: turns the server's 409 into copy a
          // user can act on. Every other onError still uses 'showError'.
          actions: ['showDeleteError'],
        },
      },
    },
    // STEP 1
    $_BASIC_INFO: {
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[1],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: '$_VIEW',
          actions: 'closeModal',
        },
      },
    },
    // STEP 2
    $_FACILITIES: {
      // [MCV-1889] Leaving the step (the modal closed mid-save, say) cancels an unfinished coverage
      // save so its late result cannot land on whichever listing the wizard opens next.
      exit: ({ context }) => context.coverageStepRef.send({ type: 'ON_RESET' }),
      entry: [
        ({ context }) =>
          context.facilitiesStepRef.send({
            type: 'ON_LOAD',
            payload: { listingId: context.listing?.id || -1 },
          }),
        // [MCV-1889] A selling listing's step 2 is the coverage step, which saves through its own
        // actor and needs the listing id the same way.
        ({ context }) =>
          context.coverageStepRef.send({
            type: 'ON_LOAD',
            payload: { listingId: context.listing?.id || -1 },
          }),
      ],
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[2],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: WIZARD_STEPS[0],
          actions: 'prevStep',
        },
      },
    },
    // STEP 3
    $_LABOR_METRICS: {
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[3],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: WIZARD_STEPS[1],
          actions: 'prevStep',
        },
      },
    },
    // STEP 4
    $_COMPENSATION: {
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[4],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: WIZARD_STEPS[2],
          actions: 'prevStep',
        },
      },
    },
    // STEP 5
    $_SHIFT_DETAILS: {
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[5],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: WIZARD_STEPS[3],
          actions: 'prevStep',
        },
        ON_SKIP: {
          target: WIZARD_STEPS[5],
          actions: 'nextStep',
        },
      },
    },
    // STEP 6
    $_COST_CALCULATOR: {
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[6],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: WIZARD_STEPS[4],
          actions: 'prevStep',
        },
      },
    },
    // STEP 7
    $_ADDITIONAL_INFO: {
      on: {
        ON_NEXT: {
          target: WIZARD_STEPS[7],
          actions: 'nextStep',
        },
        ON_PREV: {
          target: WIZARD_STEPS[5],
          actions: 'prevStep',
        },
        ON_SKIP: {
          target: WIZARD_STEPS[7],
          actions: 'nextStep',
        },
      },
    },
    $_REVIEW: {
      invoke: {
        src: 'getSummary',
        input: ({ context }) => ({
          context,
          event: {
            type: 'ON_GET_SUMMARY',
            payload: { listing: context.listing },
          },
        }),
        onDone: {
          actions: [assign({ summary: ({ event }) => event.output })],
        },
        onError: {
          actions: ['showError', assign({ summary: () => undefined })],
        },
      },
      on: {
        ON_NEXT: {
          target: '$_VIEW',
          actions: ['resetStep', 'closeModal'],
        },
        ON_PREV: {
          target: WIZARD_STEPS[6],
          actions: 'prevStep',
        },
      },
    },
  },
});
